import { useState, useEffect, useCallback } from 'react';
import { CategoryGroup, VerificationJob } from '../types';
import { apiService } from '../services/api';
import toast from 'react-hot-toast';
import { Modal, ModalFooter } from './Modal';

interface VerificationState {
  jobId: string;
  status: VerificationJob;
}

export function CategoryGroupView() {
  const [groups, setGroups] = useState<CategoryGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [verifying, setVerifying] = useState<Record<string, VerificationState | 'starting'>>({});
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await apiService.getContactsByCategory();
      setGroups(data);
    } catch (err) {
      console.error('Erro ao carregar grupos:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll active verification jobs
  useEffect(() => {
    const activeJobs = Object.entries(verifying).filter(
      ([, v]) => v !== 'starting' && v.status.inProgress
    ) as [string, VerificationState][];

    if (activeJobs.length === 0) return;

    const interval = setInterval(async () => {
      for (const [categoriaKey, state] of activeJobs) {
        try {
          const updated = await apiService.getVerifyStatus(state.jobId);
          setVerifying(prev => ({
            ...prev,
            [categoriaKey]: { jobId: state.jobId, status: updated }
          }));

          if (!updated.inProgress) {
            // Refresh groups to show updated counters
            load();
          }
        } catch {}
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [verifying, load]);

  const handleVerify = async (categoriaId: string) => {
    setVerifying(prev => ({ ...prev, [categoriaId]: 'starting' }));
    try {
      const { jobId } = await apiService.startVerifyCategory(categoriaId);
      const status = await apiService.getVerifyStatus(jobId);
      setVerifying(prev => ({ ...prev, [categoriaId]: { jobId, status } }));
    } catch (err) {
      setVerifying(prev => {
        const next = { ...prev };
        delete next[categoriaId];
        return next;
      });
      alert(err instanceof Error ? err.message : 'Erro ao iniciar verificação');
    }
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await apiService.deleteCategory(id);
      toast.success('Categoria e seus números foram excluídos');
      setExpanded(prev => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      setConfirmDeleteId(null);
      await load();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erro ao excluir categoria';
      toast.error(msg);
    }
  };

  const toggleExpand = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const getVerifyState = (categoriaId: string) => {
    const v = verifying[categoriaId];
    if (!v) return null;
    if (v === 'starting') return { loading: true, job: null };
    return { loading: false, job: v.status };
  };

  const statusIcon = (status: string | null | undefined) => {
    if (status === 'VALID') return <span className="text-green-500 text-xs font-bold">✓</span>;
    if (status === 'INVALID') return <span className="text-red-400 text-xs font-bold">✗</span>;
    return <span className="text-gray-300 text-xs">–</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderBottomColor: 'var(--astra-dark-blue)' }} />
        <span className="ml-2 text-gray-500">Carregando...</span>
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="text-center py-16 text-gray-500">
        Nenhum contato cadastrado.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {groups.map(group => {
        const key = group.categoria?.id ?? '__sem_categoria__';
        const isExpanded = expanded.has(key);
        const vState = getVerifyState(key);
        const isVerifying = vState?.loading || vState?.job?.inProgress;

        return (
          <div key={key} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            {/* Header row */}
            <div className="flex items-center gap-3 px-4 py-3">
              {/* Color dot */}
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{ backgroundColor: group.categoria?.cor ?? '#9ca3af' }}
              />

              {/* Name + count */}
              <span className="font-medium text-gray-800 flex-1">
                {group.categoria?.nome ?? 'Sem categoria'}
              </span>
              <span className="text-sm text-gray-500 mr-2">
                {group.total} número{group.total !== 1 ? 's' : ''}
              </span>

              {/* WhatsApp validation counters */}
              {(group.valid > 0 || group.invalid > 0) && (
                <div className="flex items-center gap-2 text-xs mr-2">
                  {group.valid > 0 && (
                    <span className="text-green-600 font-medium">✓ {group.valid}</span>
                  )}
                  {group.invalid > 0 && (
                    <span className="text-red-400 font-medium">✗ {group.invalid}</span>
                  )}
                  {group.unchecked > 0 && (
                    <span className="text-gray-400">– {group.unchecked}</span>
                  )}
                </div>
              )}

              {/* Verify button */}
              {group.categoria && (
                <button
                  onClick={() => !isVerifying && handleVerify(group.categoria!.id)}
                  disabled={!!isVerifying}
                  className={`text-sm font-medium mr-2 focus:outline-none transition-colors ${
                    isVerifying
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-blue-600 hover:text-blue-800'
                  }`}
                  title="Verificar se os números existem no WhatsApp"
                >
                  {isVerifying ? (
                    <span className="flex items-center gap-1">
                      <span className="animate-spin inline-block w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full" />
                      {vState?.job
                        ? `${vState.job.checked}/${vState.job.total}`
                        : 'Iniciando...'}
                    </span>
                  ) : (
                    'Verificar WhatsApp'
                  )}
                </button>
              )}

              {/* Delete category button */}
              {group.categoria && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setConfirmDeleteId(group.categoria!.id);
                  }}
                  className="text-red-600 hover:text-red-800 text-xs font-medium mr-2"
                >
                  Excluir categoria e números
                </button>
              )}

              {/* Expand/collapse */}
              <button
                onClick={() => toggleExpand(key)}
                className="text-gray-400 hover:text-gray-600 transition-colors focus:outline-none"
                aria-label={isExpanded ? 'Recolher' : 'Expandir'}
              >
                <svg
                  className={`w-5 h-5 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                  fill="none" viewBox="0 0 24 24" stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            </div>

            {/* Progress bar while verifying */}
            {isVerifying && vState?.job && (
              <div className="px-4 pb-2">
                <div className="w-full bg-gray-100 rounded-full h-1.5">
                  <div
                    className="bg-blue-500 h-1.5 rounded-full transition-all"
                    style={{ width: `${(vState.job.checked / vState.job.total) * 100}%` }}
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  {vState.job.valid} válidos · {vState.job.invalid} inválidos
                </p>
              </div>
            )}

            {/* Result message after finish */}
            {!isVerifying && vState?.job && !vState.job.inProgress && (
              <div className="px-4 pb-2 text-xs text-gray-500">
                Verificação concluída: <span className="text-green-600 font-medium">{vState.job.valid} válidos</span> · <span className="text-red-400 font-medium">{vState.job.invalid} inválidos</span>
              </div>
            )}

            {/* Expanded contact list */}
            {isExpanded && (
              <div className="border-t border-gray-100 divide-y divide-gray-50">
                {group.contacts.map(c => (
                  <div key={c.id} className="flex items-center gap-3 px-5 py-2 text-sm text-gray-600">
                    <span className="w-4 text-center">{statusIcon(c.whatsappStatus)}</span>
                    <span className="flex-1 font-medium text-gray-800">{c.nome}</span>
                    <span className="text-gray-400">{c.telefone}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {confirmDeleteId && (
        <Modal isOpen={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)} title="Confirmar exclusão" size="md">
          <p className="text-sm text-gray-700">Tem certeza que deseja excluir esta categoria e todos os números associados? Esta ação não pode ser desfeita.</p>
          <ModalFooter>
            <button
              onClick={() => setConfirmDeleteId(null)}
              className="bg-gray-100 text-gray-700 py-2 px-4 rounded-xl hover:bg-gray-200 border border-gray-200"
            >
              Cancelar
            </button>
            <button
              onClick={() => confirmDeleteId && handleDeleteCategory(confirmDeleteId)}
              className="bg-red-600 text-white py-2 px-4 rounded-xl hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
            >
              Excluir
            </button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
}
